import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { deviceColumns } from './devices-table-component/columns/columns.tsx';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import CodeDialogForm from '../code-dialog/code-dialog-form.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import useDeviceStore from '../../store/devices.store.ts';
import { ICONS } from '@shared/components/icons/icon.tsx';
import AddScreenDialogForm from '../add-screen-dialog/add-screen-dialog.tsx';
import { getSignageDevices } from '@shared/api/digital-signage/devices-api.ts';
import {
  DeviceLocationEnum,
  DevicesRowDataProps
} from '@modules/digital-signage/model/devices.model.ts';
import { MoveDevicesToGroupReqModel } from '@models/digital-signage/device-groups.model.ts';
import MoveToGroupDialogForm from '../move-to-group-dialog/move-to-group-dialog-form.tsx';
import DevicesExtraButtons from './devices-table-component/extra-buttons/devices-extra-buttons.tsx';
import ViewControls from '@components/view-controls/view-controls.tsx';
import { getDeviceKey } from '@modules/digital-signage/utils/digital-signage.utils.ts';
import useTableSelection from '@hooks/use-table-selection.ts';
import { SortOrder } from '@models/table.model.ts';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';

const DeviceTable: React.FC = () => {
  const {
    deleteRow,
    getScreens,
    activateDeviceWithCode,
    screensData,
    reloadDevices,
    isLoading,
    deviceGroupsData,
    getDeviceGroupsOptions,
    moveDevicesToGroup
  } = useDeviceStore();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tableStore = useMemo(() => createTableStore(), []);
  const {
    selectedItems: selectedDevices,
    selectedKeys: selectedRows,
    setSelectedItems: setSelectedDevices,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected,
    toggleRow: handleSelectRow,
    togglePage: handleSelectAll,
    clearSelection
  } = useTableSelection<DevicesRowDataProps>({
    tableStore,
    getRowKey: getDeviceKey
  });
  const [view, setView] = useState(ViewControlsEnum.List);

  const [openCodeDialog, setOpenCodeDialog] = useState<boolean>(false);
  const [openChromeCastDialog, setChromeCastDialog] = useState<boolean>(false);
  const [openMoveToGroupDialog, setOpenMoveToGroupDialog] =
    useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<DevicesRowDataProps | null>(
    null
  );
  const [devicesToMove, setDevicesToMove] = useState<DevicesRowDataProps[]>([]);
  const [moveSourceDevices, setMoveSourceDevices] = useState<
    DevicesRowDataProps[]
  >([]);
  const [isRowMove, setIsRowMove] = useState<boolean>(false);

  const moveSourceKeys = useMemo(
    () => new Set(moveSourceDevices.map(getDeviceKey)),
    [moveSourceDevices]
  );
  const count = selectedRows.length;

  const reloadDevice = (row?: DevicesRowDataProps) => {
    const ids = row?.deviceId ? [row.deviceId] : selectedRows;
    if (!ids.length) return;

    if (row?.deviceId) {
      clearSelection();
    }

    reloadDevices({ ids, deviceLocation: DeviceLocationEnum.Signage }, () =>
      clearSelection()
    );
  };

  const handleDelete = (row?: DevicesRowDataProps) => {
    setShowDeleteDialog(true);
    setSelectedItem(row || null);
  };

  const handleMoveToGroup = (row?: DevicesRowDataProps) => {
    const devices = row ? [row] : selectedDevices;

    setMoveSourceDevices(devices);
    setDevicesToMove(devices);
    setIsRowMove(!!row);
    setOpenMoveToGroupDialog(true);
  };

  const handleDevicesToMoveChange = (devices: DevicesRowDataProps[]) => {
    setDevicesToMove(devices);
    if (!isRowMove) {
      setSelectedDevices(devices);
      return;
    }

    const keys = new Set(devices.map(getDeviceKey));

    setSelectedDevices((prev) =>
      prev.filter(
        (device) =>
          keys.has(getDeviceKey(device)) ||
          !moveSourceKeys.has(getDeviceKey(device))
      )
    );
  };

  const handleMoveToGroupSave = (body: MoveDevicesToGroupReqModel) => {
    moveDevicesToGroup({
      body,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleDeleteConfirm = () => {
    const ids = selectedItem?.deviceId ? [selectedItem.deviceId] : selectedRows;
    if (!ids.length) return;

    deleteRow({
      data: { ids, deviceLocation: DeviceLocationEnum.Signage },
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleFormSave = (data: { code: string }) => {
    activateDeviceWithCode({
      data,
      state: tableStore.getState(),
      handleClose: () => setOpenCodeDialog(false)
    });
  };

  const handleAddScreen = (row: DevicesRowDataProps) => {
    setChromeCastDialog(true);
    setSelectedItem(row);
  };

  const handleClose = () => {
    setShowDeleteDialog(false);
    setOpenCodeDialog(false);
    setChromeCastDialog(false);
    setOpenMoveToGroupDialog(false);
    setSelectedItem(null);
    setDevicesToMove([]);
    setMoveSourceDevices([]);
    setIsRowMove(false);
    clearSelection();
  };

  const handleAction = (action: { type: string; value?: string }) => {
    switch (action.type) {
      case ViewControlsEnum.View:
        setView(action.value as ViewControlsEnum);
        break;
      case ViewControlsEnum.Delete:
        handleDelete();
        break;
      case ViewControlsEnum.Reload:
        reloadDevice();
        break;
    }
  };

  const columns = deviceColumns({
    reloadDevice,
    handleDelete,
    handleAddScreen,
    handleMoveToGroup,
    handleSelectRow,
    handleSelectAll,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected,
    deviceGroupsData
  });

  useEffect(() => {
    getScreens();
    getDeviceGroupsOptions();
  }, []);

  useEffect(() => {
    if (searchParams.get('dialog') === 'open') {
      setOpenCodeDialog(true);
    }
  }, []);

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls
          showReload={true}
          showDelete={true}
          showLanguageSelect={false}
          onAction={handleAction}
          selectedRows={selectedRows}
          extraButtons={
            <DevicesExtraButtons
              selectedRows={selectedRows}
              onMoveToGroup={() => handleMoveToGroup()}
            />
          }
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={() => setOpenCodeDialog(true)}
        >
          {t('enter_code')}
        </Button>
      </div>

      <div className={TABLE_HEIGHT.WITH_TABS_AND_ACTIONS}>
        {view === ViewControlsEnum.List && (
          <DynamicTable
            tableStore={tableStore}
            apiFunction={getSignageDevices}
            columns={columns}
            hideEmptyButton
            tableKey="digital-signage-devices-table"
            initialState={{
              sort: ['createdAt', SortOrder.Descending]
            }}
          />
        )}
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openCodeDialog}
          size="sm"
          title={t('devices_enter_code_text')}
          content={<CodeDialogForm onSave={handleFormSave} />}
          onClose={handleClose}
        />

        <AppDialog
          open={openChromeCastDialog}
          title={t('label_assign_screen')}
          content={
            <AddScreenDialogForm
              handleClose={handleClose}
              screensList={screensData}
              tableStore={tableStore}
              selectedItem={selectedItem}
            />
          }
          onClose={handleClose}
        />

        <AppDialog
          open={openMoveToGroupDialog}
          width={720}
          title={t('move_to_group')}
          content={
            <MoveToGroupDialogForm
              sourceDevices={moveSourceDevices}
              selectedDevices={devicesToMove}
              deviceGroupsData={deviceGroupsData}
              onDevicesChange={handleDevicesToMoveChange}
              onSave={handleMoveToGroupSave}
            />
          }
          onClose={handleClose}
        />

        <div className="w-full h-full">
          <DeleteDialog
            onCancel={handleClose}
            onDelete={handleDeleteConfirm}
            open={showDeleteDialog}
            isLoading={isLoading()}
            title={
              selectedItem?.deviceId
                ? t('delete_popup_title')
                : t('delete_popup_title_many_items', { count })
            }
          />
        </div>
      </div>
    </div>
  );
};

export default DeviceTable;
